import { VALUATION_OBJECT_TYPES_WITHOUT_USAGE } from './valuation.schemas';
import type { ValuationUnitBreakdown, ValuationUsage, ValuationObjectType } from './valuation.schemas';

// The single source of truth for which type-specific fields belong to which
// object type × usage (IMM-407.2.2). Consumed by the backend (to enforce the
// invariant — nothing outside the resolved set is persisted) and by the frontend
// (to mount the right blocks), so the two cannot drift.
//
// These keys are the camelCase names of the wide type-specific columns on
// `valuation_request`; a field shared across object types (year built, plot area,
// heating type, …) is one key, which is what makes the switch-retain automatic and
// the switch-diff a plain set operation.

// The full set of type-specific field keys — the columns the aggregate nulls
// when they fall outside the resolved set. Order is stable for iteration.
export const ALL_VALUATION_FIELD_KEYS = [
  // shared
  'yearBuilt',
  'plotArea',
  'rooms',
  'heatingType',
  'energyEfficiencyClass',
  'lastModernisation',
  'modernisationsDone',
  // condominium
  'livingArea',
  'floor',
  'hasLift',
  // house
  'houseType',
  'basement',
  // multi-family house
  'residentialUnits',
  'commercialUnits',
  'totalLivingArea',
  'commercialArea',
  'deferredMaintenance',
  'unitBreakdown',
  // plot
  'developmentPlan',
  'groundAreaRatio',
  'floorAreaRatio',
  'permittedDevelopment',
  'servicingStatus',
  'positivePreliminaryEnquiry',
  'hasBuildingPermit',
  'hasExistingBuilding',
  'estimatedDemolitionCost',
  'contaminationSuspicion',
  // income (actual)
  'actualRentMonthly',
  'serviceCharges',
  'occupancyRate',
  'tenancyStart',
  'graduatedIndexRent',
  // homeowners association
  'monthlyServiceCharge',
  'nonRecoverableShare',
  'maintenanceReserve',
  'coOwnershipShare',
  'resolvedSpecialLevies',
  // overall condition (IMM-407.2.3) — the built-type 4×2 rating matrix. In the
  // resolved set for condominium/house/multi-family, absent for
  // plot/commercial/garage, so a switch away from a built type discards them
  // through `discardedValuationFields`.
  'kitchenQuality',
  'kitchenCondition',
  'bathroomQuality',
  'bathroomCondition',
  'flooringQuality',
  'flooringCondition',
  'windowsQuality',
  'windowsCondition',
] as const;

export type ValuationFieldKey = (typeof ALL_VALUATION_FIELD_KEYS)[number];

// Every type-specific field null — the shape of an unfilled type-field group.
// Reused by the frontend (form defaults / test fixtures) and mirrored by the
// backend aggregate's own EMPTY_TYPE_FIELDS.
export const EMPTY_VALUATION_TYPE_FIELDS: Record<ValuationFieldKey, null> = Object.fromEntries(
  ALL_VALUATION_FIELD_KEYS.map((key) => [key, null]),
) as Record<ValuationFieldKey, null>;

// --- Per-object-type base sets (before the conditional blocks) ----------------
// Each block is listed explicitly (per the PRD `## Fields` per-type tables), so
// that a field shared across object types is the same key in each — the property
// that makes switch-retain automatic.

// Overall condition — the fixed 4-element × 2-axis rating matrix, shared by every
// built object type (condominium/house/multi-family). Absent from a plot (AC 13).
// Listed once and spread into each built set so the switch-diff names the ratings
// automatically.
const OVERALL_CONDITION: ValuationFieldKey[] = [
  'kitchenQuality',
  'kitchenCondition',
  'bathroomQuality',
  'bathroomCondition',
  'flooringQuality',
  'flooringCondition',
  'windowsQuality',
  'windowsCondition',
];

// Condominium: living data + no plot area (it is a unit).
const CONDOMINIUM_FIELDS: ValuationFieldKey[] = [
  'yearBuilt',
  'rooms',
  'heatingType',
  'energyEfficiencyClass',
  'lastModernisation',
  'modernisationsDone',
  'livingArea',
  'floor',
  'hasLift',
  ...OVERALL_CONDITION,
];

// Single-family / terraced house: adds plot area, house type, basement; drops
// floor / lift (condominium-only).
const HOUSE_FIELDS: ValuationFieldKey[] = [
  'yearBuilt',
  'plotArea',
  'rooms',
  'heatingType',
  'energyEfficiencyClass',
  'lastModernisation',
  'modernisationsDone',
  'livingArea',
  'houseType',
  'basement',
  ...OVERALL_CONDITION,
];

// Multi-family house: unit/area counts + deferred maintenance (not modernisations
// done) + the aggregate/individual switch; no rooms / living area.
const MULTI_FAMILY_FIELDS: ValuationFieldKey[] = [
  'yearBuilt',
  'plotArea',
  'heatingType',
  'energyEfficiencyClass',
  'lastModernisation',
  'residentialUnits',
  'commercialUnits',
  'totalLivingArea',
  'commercialArea',
  'deferredMaintenance',
  'unitBreakdown',
  ...OVERALL_CONDITION,
];

// Plot: buildability + servicing; no building fields, no usage.
const PLOT_FIELDS: ValuationFieldKey[] = [
  'plotArea',
  'developmentPlan',
  'groundAreaRatio',
  'floorAreaRatio',
  'permittedDevelopment',
  'servicingStatus',
  'positivePreliminaryEnquiry',
  'hasBuildingPermit',
  'hasExistingBuilding',
  'estimatedDemolitionCost',
  'contaminationSuspicion',
];

// Income (actual) — conditional (rented/partially rented, or multi-family house).
const INCOME: ValuationFieldKey[] = [
  'actualRentMonthly',
  'serviceCharges',
  'occupancyRate',
  'tenancyStart',
  'graduatedIndexRent',
];

// Homeowners association — conditional (condominium).
const HOA: ValuationFieldKey[] = [
  'monthlyServiceCharge',
  'nonRecoverableShare',
  'maintenanceReserve',
  'coOwnershipShare',
  'resolvedSpecialLevies',
];

// --- The selection + result types ---------------------------------------------

export type ValuationFieldSetSelection = {
  readonly objectType: ValuationObjectType | null;
  readonly usage: ValuationUsage | null;
  // Only relevant to the multi-family per-unit list (`showUnits`); omitted for the
  // flat field-set diff, which does not depend on it.
  readonly unitBreakdown?: ValuationUnitBreakdown | null;
};

export type ResolvedValuationFieldSet = {
  // `false` for commercial property / garage / parking space — their intake form
  // is deferred (Resolved Decision 20). The API rejects type-specific fields for
  // them and the UI shows the "not yet available" panel. `fields` is then always
  // empty.
  readonly available: boolean;
  readonly fields: ValuationFieldKey[];
  // The repeatable-collection block flags (IMM-407.2.3), driven off the same
  // selection so the frontend mounts and the backend clears from one source.
  // `showParkingSpaces` — the parking-spaces list applies (built object types only).
  readonly showParkingSpaces: boolean;
  // `showUnits` — the per-unit rent roll applies (multi-family house with unit
  // breakdown = INDIVIDUAL only).
  readonly showUnits: boolean;
};

// The built object types that carry the overall-condition + parking-spaces blocks.
const BUILT_OBJECT_TYPES: ReadonlyArray<ValuationObjectType> = [
  'CONDOMINIUM',
  'SINGLE_FAMILY_TERRACED_HOUSE',
  'MULTI_FAMILY_HOUSE',
];

// Usage states that put the income (actual) block in the resolved set.
const RENTED_USAGES: ReadonlyArray<ValuationUsage> = ['RENTED', 'PARTIALLY_RENTED'];

/**
 * Resolve the type-specific field set for an object type × usage (IMM-407.2.2).
 * The single source of truth behind both the backend invariant and the frontend
 * rendering.
 *
 * - Condominium → its fields + the HOA block (AC 12); income when let.
 * - Single-family / terraced house → its fields; income when let.
 * - Multi-family house → its fields + the income block always (AC 11).
 * - Plot → its fields only; no usage, no income, no HOA (AC 14).
 * - Commercial / garage / parking space → `available: false`, no fields (RD 20).
 * - `null` object type (nothing picked yet) → available, no type-specific fields.
 */
export function resolveValuationFieldSet(
  objectType: ValuationObjectType | null,
  usage: ValuationUsage | null,
  unitBreakdown: ValuationUnitBreakdown | null = null,
): ResolvedValuationFieldSet {
  // The parking-spaces block shows for every built object type; the per-unit rent
  // roll only for a multi-family house filed unit-by-unit. Both computed off the
  // same selection so the frontend mounts and the backend clears from one source.
  const showParkingSpaces = objectType !== null && BUILT_OBJECT_TYPES.includes(objectType);
  const showUnits = objectType === 'MULTI_FAMILY_HOUSE' && unitBreakdown === 'INDIVIDUAL';

  switch (objectType) {
    case null:
      return { available: true, fields: [], showParkingSpaces, showUnits };
    case 'CONDOMINIUM':
      return {
        available: true,
        fields: [...CONDOMINIUM_FIELDS, ...HOA, ...incomeWhenLet(usage)],
        showParkingSpaces,
        showUnits,
      };
    case 'SINGLE_FAMILY_TERRACED_HOUSE':
      return { available: true, fields: [...HOUSE_FIELDS, ...incomeWhenLet(usage)], showParkingSpaces, showUnits };
    case 'MULTI_FAMILY_HOUSE':
      // A multi-family house is let by definition — income always applies (AC 11),
      // regardless of the usage value.
      return { available: true, fields: [...MULTI_FAMILY_FIELDS, ...INCOME], showParkingSpaces, showUnits };
    case 'PLOT':
      // Usage is ignored — a plot carries none and never an income block.
      return { available: true, fields: [...PLOT_FIELDS], showParkingSpaces, showUnits };
    case 'COMMERCIAL_PROPERTY':
    case 'GARAGE_PARKING_SPACE':
      return { available: false, fields: [], showParkingSpaces, showUnits };
  }
}

function incomeWhenLet(usage: ValuationUsage | null): ValuationFieldKey[] {
  return usage !== null && RENTED_USAGES.includes(usage) ? [...INCOME] : [];
}

// --- Mandatory-field validation (IMM-407.3) -----------------------------------
// The single shared validator behind the preview flag (frontend) and the submit
// guard (backend), so the two cannot drift. The mandatory subset is the true
// minimum from the PRD `## Fields` `Mandatory: yes` column — everything else is
// optional and never blocks submission.

// The always-visible base fields that can be mandatory. Not type-specific field
// keys (they live on the aggregate's Basics block, not the wide type columns), so
// the offender list is typed over this widened key space.
export const VALUATION_MANDATORY_BASE_KEYS = ['objectType', 'usage', 'address'] as const;
export type ValuationMandatoryBaseKey = (typeof VALUATION_MANDATORY_BASE_KEYS)[number];

// The offender keys `missingMandatoryValuationFields` can return: the mandatory
// base fields plus any type-specific field key.
export type ValuationMandatoryFieldKey = ValuationMandatoryBaseKey | ValuationFieldKey;

// The candidate mandatory type-specific fields per object type (PRD `## Fields`).
// `actualRentMonthly` and `monthlyServiceCharge` are listed for the types whose
// block can carry them, then intersected with the resolved set — so
// `actualRentMonthly` is enforced exactly where the income block is in-set (a let
// condominium/house, and always a multi-family house — individual/unit-by-unit
// filing does NOT waive it), and `monthlyServiceCharge` exactly where the HOA
// block is in-set (a condominium).
const MANDATORY_TYPE_FIELDS_BY_OBJECT_TYPE: Record<ValuationObjectType, ValuationFieldKey[]> = {
  CONDOMINIUM: ['livingArea', 'yearBuilt', 'monthlyServiceCharge', 'actualRentMonthly'],
  SINGLE_FAMILY_TERRACED_HOUSE: ['livingArea', 'plotArea', 'yearBuilt', 'actualRentMonthly'],
  MULTI_FAMILY_HOUSE: ['residentialUnits', 'totalLivingArea', 'plotArea', 'yearBuilt', 'actualRentMonthly'],
  PLOT: ['plotArea', 'developmentPlan', 'servicingStatus'],
  // Intake deferred (available: false) — not submittable at all (see below).
  COMMERCIAL_PROPERTY: [],
  GARAGE_PARKING_SPACE: [],
};

/**
 * The mandatory fields still empty for the current selection (IMM-407.3 AC 29) —
 * the empty offenders the preview names and the backend submit guard refuses on.
 * Returns an EMPTY array when everything mandatory is present (submittable).
 *
 * - `objectType` is always mandatory; when it is unset nothing else can be
 *   resolved, so it is the sole offender.
 * - An object type whose intake is deferred (commercial / garage,
 *   `available: false`) is never submittable — `objectType` is flagged as the
 *   blocker so the guard refuses rather than silently allowing a submit.
 * - `address` is always mandatory; `usage` is mandatory unless the object type
 *   carries none (plot / garage).
 * - The per-type mandatory type-specific fields are intersected with the resolved
 *   set, so a field is only demanded where its block actually applies.
 */
export function missingMandatoryValuationFields(
  objectType: ValuationObjectType | null,
  usage: ValuationUsage | null,
  unitBreakdown: ValuationUnitBreakdown | null,
  values: Partial<Record<ValuationMandatoryFieldKey, unknown>>,
): ValuationMandatoryFieldKey[] {
  // No object type yet → cannot resolve the set; the object type itself is the
  // only offender.
  if (objectType === null) return ['objectType'];

  const resolved = resolveValuationFieldSet(objectType, usage, unitBreakdown);

  // Deferred intake (commercial / garage) — unsubmittable; flag the object type.
  if (!resolved.available) return ['objectType'];

  const missing: ValuationMandatoryFieldKey[] = [];

  if (!hasValue(values.address)) missing.push('address');

  if (!VALUATION_OBJECT_TYPES_WITHOUT_USAGE.includes(objectType) && usage === null) missing.push('usage');

  const inSet = new Set(resolved.fields);

  for (const key of MANDATORY_TYPE_FIELDS_BY_OBJECT_TYPE[objectType]) {
    if (inSet.has(key) && !hasValue(values[key])) missing.push(key);
  }

  return missing;
}

// --- Preview / admin-detail block layout (IMM-407.3) --------------------------
// The single ordered block → field-key grouping the web-app preview renders
// read-only and the IMM-407.4 admin detail reuses, so the two surfaces show the
// same structure. Pure over the same object type × usage × unit-breakdown
// selection as the resolver.

export type ValuationBlockId =
  | 'basics'
  | 'reason'
  | 'condominium'
  | 'house'
  | 'multiFamily'
  | 'plot'
  | 'overallCondition'
  | 'parkingSpaces'
  | 'income'
  | 'units'
  | 'hoa'
  | 'documents';

// The always-visible Basics + Reason keys — base/context fields, not type columns.
export const VALUATION_LAYOUT_BASE_KEYS = ['objectType', 'usage', 'address'] as const;
// `priceExpectations` is a marker, not a scalar field: the surface expands it into
// one row per kind, the same way `parkingSpaces` / `units` /
// `documents` mark a list. It keeps the slot the flat owner columns held, so the
// preview order still matches the form.
export const VALUATION_LAYOUT_CONTEXT_KEYS = [
  'valuationReason',
  'timeHorizon',
  'desiredCompletion',
  'priceExpectations',
  'note',
] as const;

// Every key that can appear under a block: the base + context fields, the
// type-specific field keys, and the collection / documents markers.
export type ValuationBlockFieldKey =
  | (typeof VALUATION_LAYOUT_BASE_KEYS)[number]
  | (typeof VALUATION_LAYOUT_CONTEXT_KEYS)[number]
  | ValuationFieldKey
  | 'parkingSpaces'
  | 'units'
  | 'documents';

/**
 * The block fields whose values are monetary. Declared here, beside the field
 * set that owns them, so no read surface has to infer a unit from a value's
 * JavaScript type — the omission that made every amount render bare (IMM-644).
 *
 * `priceExpectations` (a layout marker the surface expands into rows) and a
 * rent-roll unit's `currentRent` (which lives on the unit, not the block) are
 * monetary too, but are not block fields: their call sites format directly.
 */
export const VALUATION_CURRENCY_FIELD_KEYS = [
  'actualRentMonthly',
  'serviceCharges',
  'monthlyServiceCharge',
  'nonRecoverableShare',
  'maintenanceReserve',
  'estimatedDemolitionCost',
] as const satisfies ReadonlyArray<ValuationBlockFieldKey>;

export type ValuationCurrencyFieldKey = (typeof VALUATION_CURRENCY_FIELD_KEYS)[number];

/**
 * The block fields whose values are areas, rendered with an `m²` suffix. Same
 * reason and same shape as {@link VALUATION_CURRENCY_FIELD_KEYS}: the unit
 * belongs beside the field set that owns the keys, never in whichever surface
 * happens to render them — declaring it per-surface is what let the customer
 * Vorschau and the operator detail drop the same unit twice (IMM-681).
 */
export const VALUATION_AREA_FIELD_KEYS = [
  'livingArea',
  'plotArea',
  'totalLivingArea',
  'commercialArea',
] as const satisfies ReadonlyArray<ValuationBlockFieldKey>;

export type ValuationAreaFieldKey = (typeof VALUATION_AREA_FIELD_KEYS)[number];

/**
 * The block fields whose values are percentages, rendered with a `%` suffix.
 * `occupancyRate` (Vermietungsquote) is the only one today; the set exists so
 * the second one is an entry here rather than a fifth branch in two renderers.
 */
export const VALUATION_PERCENT_FIELD_KEYS = ['occupancyRate'] as const satisfies ReadonlyArray<ValuationBlockFieldKey>;

export type ValuationPercentFieldKey = (typeof VALUATION_PERCENT_FIELD_KEYS)[number];

/**
 * The block fields whose values are ISO date strings, rendered as a localised
 * date. Moved here from the two frontends, which each declared their own copy.
 */
export const VALUATION_DATE_FIELD_KEYS = [
  'desiredCompletion',
  'tenancyStart',
] as const satisfies ReadonlyArray<ValuationBlockFieldKey>;

export type ValuationDateFieldKey = (typeof VALUATION_DATE_FIELD_KEYS)[number];

/**
 * The block fields whose values are years. A year is an ordinal label, not a
 * quantity: it renders bare — never grouped as `1.904` (IMM-444), and never
 * with a unit suffix. Moved here from the two frontends for the same reason as
 * the date set above.
 */
export const VALUATION_YEAR_FIELD_KEYS = [
  'yearBuilt',
  'lastModernisation',
] as const satisfies ReadonlyArray<ValuationBlockFieldKey>;

export type ValuationYearFieldKey = (typeof VALUATION_YEAR_FIELD_KEYS)[number];

export type ValuationLayoutBlock = {
  readonly block: ValuationBlockId;
  readonly fieldKeys: ValuationBlockFieldKey[];
};

// The core object-type block field keys (WITHOUT overall condition / income / HOA,
// which are their own blocks below), so each block renders exactly its own fields.
const CONDOMINIUM_CORE: ValuationFieldKey[] = [
  'yearBuilt',
  'rooms',
  'heatingType',
  'energyEfficiencyClass',
  'lastModernisation',
  'modernisationsDone',
  'livingArea',
  'floor',
  'hasLift',
];
const HOUSE_CORE: ValuationFieldKey[] = [
  'yearBuilt',
  'plotArea',
  'rooms',
  'heatingType',
  'energyEfficiencyClass',
  'lastModernisation',
  'modernisationsDone',
  'livingArea',
  'houseType',
  'basement',
];
const MULTI_FAMILY_CORE: ValuationFieldKey[] = [
  'yearBuilt',
  'plotArea',
  'heatingType',
  'energyEfficiencyClass',
  'lastModernisation',
  'residentialUnits',
  'commercialUnits',
  'totalLivingArea',
  'commercialArea',
  'deferredMaintenance',
  'unitBreakdown',
];

const OBJECT_TYPE_BLOCK: Record<ValuationObjectType, { block: ValuationBlockId; keys: ValuationFieldKey[] } | null> = {
  CONDOMINIUM: { block: 'condominium', keys: CONDOMINIUM_CORE },
  SINGLE_FAMILY_TERRACED_HOUSE: { block: 'house', keys: HOUSE_CORE },
  MULTI_FAMILY_HOUSE: { block: 'multiFamily', keys: MULTI_FAMILY_CORE },
  PLOT: { block: 'plot', keys: [...PLOT_FIELDS] },
  // Deferred intake — no object-type block.
  COMMERCIAL_PROPERTY: null,
  GARAGE_PARKING_SPACE: null,
};

/**
 * The ordered block → field-key grouping for the preview + admin detail
 * (IMM-407.3 AC 28). Basics and Reason and Documents always render; the
 * object-type block, overall condition, parking spaces, income, per-unit rent
 * roll and the HOA block render only where they are in the resolved set. Every
 * type-specific key is intersected with the resolved set so nothing outside it is
 * ever shown.
 */
export function valuationBlockLayout(
  objectType: ValuationObjectType | null,
  usage: ValuationUsage | null,
  unitBreakdown: ValuationUnitBreakdown | null = null,
): ValuationLayoutBlock[] {
  const resolved = resolveValuationFieldSet(objectType, usage, unitBreakdown);
  const inSet = new Set(resolved.fields);
  const blocks: ValuationLayoutBlock[] = [];

  // Basics — object type always, usage unless the type carries none, address.
  const basics: ValuationBlockFieldKey[] = ['objectType'];

  if (objectType === null || !VALUATION_OBJECT_TYPES_WITHOUT_USAGE.includes(objectType)) basics.push('usage');

  basics.push('address');
  blocks.push({ block: 'basics', fieldKeys: basics });

  // Reason & expectation — always present.
  blocks.push({ block: 'reason', fieldKeys: [...VALUATION_LAYOUT_CONTEXT_KEYS] });

  // The object-type block (its core fields intersected with the resolved set).
  const objectTypeBlock = objectType === null ? null : OBJECT_TYPE_BLOCK[objectType];

  if (objectTypeBlock !== null) {
    const keys = objectTypeBlock.keys.filter((key) => inSet.has(key));

    if (keys.length > 0) blocks.push({ block: objectTypeBlock.block, fieldKeys: keys });
  }

  // Overall condition — the built-type rating matrix, when in-set.
  const conditionKeys = OVERALL_CONDITION.filter((key) => inSet.has(key));

  if (conditionKeys.length > 0) blocks.push({ block: 'overallCondition', fieldKeys: conditionKeys });

  // Parking spaces — the repeatable list, for the built object types.
  if (resolved.showParkingSpaces) blocks.push({ block: 'parkingSpaces', fieldKeys: ['parkingSpaces'] });

  // Income (actual) — when the block is in-set (let, or multi-family always).
  const incomeKeys = INCOME.filter((key) => inSet.has(key));

  if (incomeKeys.length > 0) blocks.push({ block: 'income', fieldKeys: incomeKeys });

  // Per-unit rent roll — multi-family filed unit-by-unit.
  if (resolved.showUnits) blocks.push({ block: 'units', fieldKeys: ['units'] });

  // HOA block — for a condominium.
  const hoaKeys = HOA.filter((key) => inSet.has(key));

  if (hoaKeys.length > 0) blocks.push({ block: 'hoa', fieldKeys: hoaKeys });

  // Documents — always present.
  blocks.push({ block: 'documents', fieldKeys: ['documents'] });

  return blocks;
}

/**
 * The fields that would lose their value when switching from one selection to
 * another (AC 22) — the keys in `from`'s resolved set that are NOT in `to`'s and
 * that currently hold a value. The switch-warning names exactly these; anything
 * both sets share is retained silently. Pure over the same definition, so it is
 * unit-testable without the form.
 */
export function discardedValuationFields(
  from: ValuationFieldSetSelection,
  to: ValuationFieldSetSelection,
  values: Partial<Record<ValuationFieldKey, unknown>>,
): ValuationFieldKey[] {
  const fromSet = resolveValuationFieldSet(from.objectType, from.usage).fields;
  const toSet = new Set(resolveValuationFieldSet(to.objectType, to.usage).fields);

  return fromSet.filter((key) => !toSet.has(key) && hasValue(values[key]));
}

function hasValue(value: unknown): boolean {
  return value !== undefined && value !== null;
}

// The repeatable collections that can be discarded by a switch (IMM-407.2.3).
export type ValuationDiscardableCollection = 'parkingSpaces' | 'units';

/**
 * The collections that would lose their entries on a switch (IMM-407.2.3) — the
 * bespoke counterpart to `discardedValuationFields` for the two child-table
 * blocks. A collection is named only when it currently HOLDS entries AND its
 * block leaves the resolved set: parking spaces when switching off a built object
 * type (e.g. → plot), units when leaving multi-family + INDIVIDUAL (→ AGGREGATE, or
 * another object type). The frontend unions this with `discardedValuationFields`
 * for the switch-warning and clears the named collections on confirm.
 */
export function discardedValuationCollections(
  from: ValuationFieldSetSelection,
  to: ValuationFieldSetSelection,
  present: { readonly parkingSpaces: boolean; readonly units: boolean },
): ValuationDiscardableCollection[] {
  const fromResolved = resolveValuationFieldSet(from.objectType, from.usage, from.unitBreakdown ?? null);
  const toResolved = resolveValuationFieldSet(to.objectType, to.usage, to.unitBreakdown ?? null);

  const discarded: ValuationDiscardableCollection[] = [];

  if (present.parkingSpaces && fromResolved.showParkingSpaces && !toResolved.showParkingSpaces) {
    discarded.push('parkingSpaces');
  }

  if (present.units && fromResolved.showUnits && !toResolved.showUnits) discarded.push('units');

  return discarded;
}
